import React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { DataTable, ColumnHelper } from "@/components/data-table"
import { Modal, ModalContent, ModalHeader, ModalTitle } from "@/components/modal"
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/drawer"
import { SmartForm } from "@/components/smart-form"
import { ConfirmDialog } from "@/components/confirm-dialog"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import { z } from "zod"
import { GitBranch, Plus, RefreshCw, RotateCcw, Trash2, Eye } from "lucide-react"

const versionSchema = z.object({
  module: z.string().min(1, "Module is required"),
  data: z.string().min(1, "Data is required"),
  description: z.string().optional(),
})

const versionFormFields = [
  { name: "module", label: "Module", type: "text", placeholder: "e.g. settings, pricing, features" },
  { name: "data", label: "Data (JSON)", type: "text", placeholder: "Enter JSON data" },
  { name: "description", label: "Description", type: "text", placeholder: "Enter description" },
]

export default function VersionsPage() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const [state, setState] = React.useState({
    isFormOpen: false,
    isViewOpen: false,
    isDeleteOpen: false,
    selectedItem: null,
    viewItem: null,
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["versions"],
    queryFn: () => api.get("/versioning"),
  })

  const createMutation = useMutation({
    mutationFn: (formData) => {
      const payload = {
        module: formData.module,
        data: JSON.parse(formData.data),
        description: formData.description,
      }
      return api.post("/versioning", payload)
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries(["versions"])
      addNotification({ type: "success", message: res.message || "Version created successfully" })
      setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const restoreMutation = useMutation({
    mutationFn: (id) => api.post(`/versioning/${id}/restore`),
    onSuccess: (res) => {
      queryClient.invalidateQueries(["versions"])
      addNotification({ type: "success", message: res.message || "Version restored successfully" })
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/versioning/${state.selectedItem.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["versions"])
      addNotification({ type: "success", message: "Version deleted successfully" })
      setState((s) => ({ ...s, isDeleteOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const versions = data?.data || []
  const modules = [...new Set(versions.map((v) => v.module))]

  const colHelper = ColumnHelper()
  const columns = React.useMemo(() => {
    const cols = [
      colHelper.text("id", { label: "ID" }),
      colHelper.text("module", { label: "Module" }),
      colHelper.number("version_number", { label: "Version" }),
      colHelper.text("description", { label: "Description" }),
      colHelper.text("first_name", { label: "User" }),
      colHelper.date("created_at", { label: "Created" }),
      colHelper.actions([
        {
          icon: <Eye className="h-4 w-4" />,
          onClick: (row) => setState((s) => ({ ...s, isViewOpen: true, viewItem: row })),
        },
        {
          icon: <RotateCcw className="h-4 w-4" />,
          onClick: (row) => restoreMutation.mutate(row.id),
        },
        {
          icon: <Trash2 className="h-4 w-4" />,
          variant: "ghost",
          onClick: (row) => setState((s) => ({ ...s, isDeleteOpen: true, selectedItem: row })),
        },
      ]),
    ]
    return cols
  }, [])

  const formatJson = (data) => {
    try {
      return JSON.stringify(data, null, 2)
    } catch {
      return String(data)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Config Versions</h1>
          <p className="text-muted-foreground">Track and restore configuration changes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setState((s) => ({ ...s, isFormOpen: true, selectedItem: null }))}>
            <Plus className="mr-2 h-4 w-4" />
            Create Version
          </Button>
        </div>
      </div>

      {modules.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline">All Versions: {versions.length}</Badge>
          {modules.map((mod) => (
            <Badge key={mod} variant="secondary">
              {mod}: {versions.filter((v) => v.module === mod).length}
            </Badge>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        data={versions}
        searchKey="module"
        searchPlaceholder="Search versions..."
        onRefresh={refetch}
        isLoading={isLoading}
      />

      <Drawer open={state.isFormOpen} onClose={() => setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))}>
        <DrawerHeader>
          <DrawerTitle>Create Version</DrawerTitle>
        </DrawerHeader>
        <SmartForm
          schema={versionSchema}
          fields={versionFormFields}
          defaultValues={{}}
          onSubmit={(formData) => createMutation.mutate(formData)}
          onCancel={() => setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))}
          isLoading={createMutation.isPending}
        />
      </Drawer>

      <Modal open={state.isViewOpen} onOpenChange={(open) => setState((s) => ({ ...s, isViewOpen: open }))}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Version Details</ModalTitle>
          </ModalHeader>
          <div className="space-y-4">
            {state.viewItem && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Module</span>
                  <span className="font-medium">{state.viewItem.module}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Version</span>
                  <span className="font-medium">{state.viewItem.version_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Description</span>
                  <span className="font-medium">{state.viewItem.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created By</span>
                  <span className="font-medium">{state.viewItem.first_name || state.viewItem.user_id}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Data</span>
                  <pre className="mt-2 p-3 bg-muted rounded-lg text-xs overflow-auto max-h-64">
                    {formatJson(state.viewItem.data)}
                  </pre>
                </div>
              </>
            )}
          </div>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        open={state.isDeleteOpen}
        onClose={() => setState((s) => ({ ...s, isDeleteOpen: false, selectedItem: null }))}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Version"
        description="Are you sure you want to delete this version? This action cannot be undone."
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
